import { useMemo, useState } from 'react';
import { GlobeHemisphereWest, Plus, Scissors } from '@phosphor-icons/react';
import { toast } from 'sonner';

import {
  useBusinessServices,
  useCreateBusinessService,
  type BusinessServiceStatus,
} from '@/features/business-services';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { StatusBadge, type StatusVariant } from '@/shared/ui/StatusBadge';
import { formatMoney } from '@/shared/utils/formatters';

const STATUS_VARIANT: Record<BusinessServiceStatus, StatusVariant> = {
  active: 'success',
  draft: 'pending',
  archived: 'neutral',
};

export function ServicesPage(): JSX.Element {
  const { data: services, isLoading } = useBusinessServices();
  const createService = useCreateBusinessService();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('1500');
  const [duration, setDuration] = useState('30');

  const stats = useMemo(() => {
    const items = services ?? [];
    return {
      total: items.length,
      active: items.filter((s) => s.status === 'active').length,
      draft: items.filter((s) => s.status === 'draft').length,
    };
  }, [services]);

  const handleCreate = async () => {
    const displayName = name.trim();
    if (!displayName) return;
    try {
      await createService.mutateAsync({
        code: displayName.toLocaleUpperCase('tr-TR').replace(/\s+/g, '-').slice(0, 32),
        displayName,
        category: 'grooming',
        iconKey: 'grooming',
        durationMinutes: Number(duration),
        basePriceMinor: Math.round(Number(price) * 100),
        currency: 'TRY',
        status: 'active',
      });
      setName('');
      toast.success('Hizmet eklendi');
    } catch {
      toast.error('Hizmet eklenemedi');
    }
  };

  return (
    <div className="px-biz-card-lg py-biz-content-lg max-w-[1400px] mx-auto flex flex-col gap-biz-content-lg">
      <header className="flex items-start justify-between gap-biz-content-lg flex-wrap">
        <div>
          <h1 className="text-biz-2xl font-bold biz-heading">Hizmet Kataloğu</h1>
          <p className="text-biz-sm text-biz-muted-foreground mt-1">
            Şube bazlı servisleri canlı Business API üzerinden yönetin.
          </p>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-biz-content-lg">
        <Metric label="Toplam hizmet" value={stats.total} />
        <Metric label="Aktif" value={stats.active} />
        <Metric label="Taslak" value={stats.draft} />
      </section>

      <Card>
        <CardHeader>
          <div>
            <h2 className="font-semibold biz-heading">Yeni hizmet</h2>
            <p className="text-biz-sm text-biz-muted-foreground">Kasa ve randevu formlarına anında yansır.</p>
          </div>
        </CardHeader>
        <CardBody className="grid grid-cols-1 md:grid-cols-[1fr_160px_160px_auto] gap-biz-content">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn. Küçük ırk banyo" />
          <Input value={duration} onChange={(e) => setDuration(e.target.value)} type="number" min={1} placeholder="Dakika" />
          <Input value={price} onChange={(e) => setPrice(e.target.value)} type="number" min={0} placeholder="Fiyat" />
          <Button onClick={handleCreate} loading={createService.isPending} leftIcon={<Plus size={18} />}>
            Ekle
          </Button>
        </CardBody>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <h2 className="font-semibold biz-heading">Servisler</h2>
          <span className="text-biz-sm text-biz-muted-foreground">{isLoading ? 'Yükleniyor' : `${services?.length ?? 0} kayıt`}</span>
        </CardHeader>
        <div className="divide-y divide-biz-border">
          {(services ?? []).map((service) => (
            <div key={service.id} className="px-biz-card py-biz-content-lg flex items-center justify-between gap-biz-content-lg">
              <div className="flex items-center gap-biz-content-lg min-w-0">
                <div className="w-11 h-11 rounded-biz-card bg-biz-primary-subtle text-biz-primary flex items-center justify-center">
                  <Scissors size={20} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold truncate">{service.displayName}</p>
                    {service.externalOfferingId && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-biz-info-subtle px-2 py-0.5 text-[11px] font-semibold text-biz-info">
                        <GlobeHemisphereWest size={12} weight="regular" />
                        Marketplace
                      </span>
                    )}
                  </div>
                  <p className="text-biz-sm text-biz-muted-foreground">
                    {service.durationMinutes} dk · {formatMoney(service.basePriceMinor / 100, service.currency)}
                    {service.externalOfferingId ? ` · Offering ${service.externalOfferingId.slice(0, 8)}` : ''}
                  </p>
                </div>
              </div>
              <StatusBadge variant={STATUS_VARIANT[service.status]} label={service.status} />
            </div>
          ))}
          {!isLoading && (services?.length ?? 0) === 0 ? (
            <div className="px-biz-card py-biz-card text-biz-sm text-biz-muted-foreground">
              Henüz hizmet yok. İlk hizmeti ekleyerek takvim/kasa akışını canlı veriye bağlayın.
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <Card>
      <CardBody>
        <p className="text-biz-sm text-biz-muted-foreground">{label}</p>
        <p className="text-biz-2xl font-bold biz-heading mt-1">{value}</p>
      </CardBody>
    </Card>
  );
}
